/**
 * StatusBadge Component
 * 
 * Displays a colored badge to indicate the status of an invoice.
 * Used to show Paid/Unpaid and Finalized/Draft states.
 */
import React from 'react';

type BadgeVariant = 'success' | 'warning' | 'secondary' | 'danger' | 'info';

interface StatusBadgeProps {
  /** The text to display in the badge */
  label: string;
  /** The color variant of the badge */
  variant: BadgeVariant;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ label, variant }) => {
  return (
    <span className={`badge badge-${variant}`} data-testid="status-badge">
      {label}
    </span>
  );
};

export default StatusBadge;

/**
 * Helper function to get the appropriate badge for paid status
 * - Paid = Green (success)
 * - Unpaid = Red (danger) - needs attention
 */
export const getPaidBadge = (paid: boolean) => {
  return paid 
    ? { label: 'Paid', variant: 'success' as BadgeVariant }
    : { label: 'Unpaid', variant: 'danger' as BadgeVariant };
};

/**
 * Helper function to get the appropriate badge for finalized status
 * - Finalized = Purple/Blue (secondary) - completed state
 * - Draft = Gray (info) - work in progress
 */
export const getFinalizedBadge = (finalized: boolean) => {
  return finalized
    ? { label: 'Finalized', variant: 'secondary' as BadgeVariant }
    : { label: 'Draft', variant: 'info' as BadgeVariant };
};

